import React, { FC, RefObject } from 'react';

import styled from 'styled-components';

import {
  ENTITLEMENTS_PLATFORM_TYPES,
  EntitlementsProject,
} from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import {
  LogoAutodeskAccIcon,
  LogoAutodeskBim360Icon,
} from '@adsk/alloy-react-icon';
import { MenuItem } from '@adsk/alloy-react-menu';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import HighlightedItemText from './HighlightedItemText';
import { MFAIcon } from './MFAIcon';
import getAnchorRel from '../helpers/getAnchorRel';
import getAnchorTarget from '../helpers/getAnchorTarget';
import getProjectUrl from '../helpers/getProjectUrl';

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 5px;
`;

type ProjectListAccountItemProps = {
  item: EntitlementsProject;
  search?: string;
  currentProjectId?: string;
  currentProductId?: string;
  onClose?: () => void;
  onProjectClick?: (project?: EntitlementsProject, url?: string) => void;
  handleAnalyticOnClick?: (project?: EntitlementsProject) => void;
  containerRef?: RefObject<HTMLDivElement>;
};

const ProjectListProjectItem: FC<ProjectListAccountItemProps> = ({
  item: project,
  search,
  currentProjectId,
  currentProductId,
  onClose,
  onProjectClick,
  handleAnalyticOnClick,
  containerRef,
}) => {
  const { id, platform } = project;
  const url = getProjectUrl(project, currentProductId);
  const highlighted = currentProjectId === id;

  const LogoComponent =
    platform === ENTITLEMENTS_PLATFORM_TYPES.ACC
      ? LogoAutodeskAccIcon
      : LogoAutodeskBim360Icon;

  return (
    <MenuItem
      data-testid={`ProjectPicker__project-${id}`}
      rel={getAnchorRel(url)}
      highlighted={highlighted}
      showSelection={false}
      as={onProjectClick ? 'button' : 'a'}
      href={url}
      target={getAnchorTarget(url)}
      onClick={() => {
        onProjectClick?.(project, url);
        handleAnalyticOnClick?.(project);
        onClose?.();
      }}
      style={{
        height: 'auto',
        padding: '8px 16px',
        textAlign: 'initial',
      }}
    >
      <Flex flex={1} alignItems="center">
        <Box width="48px" height="48px" marginRight="16px">
          <StyledImg
            src={project.imageUrl ? project.imageUrl : undefined}
            data-testid="item-image"
          />
        </Box>
        <Box flex={1} maxWidth="216px">
          <Flex alignItems="center">
            <Flex flexDirection="column" overflow="hidden">
              <Box
                typography="heading4"
                color={highlighted ? 'white' : 'charcoal900'}
              >
                <OverflowTooltip content={project.name} singleLine={false}>
                  <HighlightedItemText
                    search={search}
                    text={project.name as string}
                    highlighted={highlighted}
                  />
                </OverflowTooltip>
              </Box>
              {project.jobNumber && (
                <Box
                  typography="caption"
                  color={highlighted ? 'white' : 'charcoal800'}
                  ellipsis="single"
                >
                  <HighlightedItemText
                    search={search}
                    text={project.jobNumber}
                    highlighted={highlighted}
                  />
                </Box>
              )}
              <Box
                typography="caption"
                color={highlighted ? 'white' : 'charcoal600'}
                ellipsis="single"
              >
                {project.accountName}
              </Box>
            </Flex>
            {project.mfaBlocked && <MFAIcon containerRef={containerRef} />}
          </Flex>
        </Box>
        <LogoComponent
          size={16}
          style={{
            color: highlighted ? theme.colors.white : theme.colors.acsLogoBlue,
            marginLeft: theme.spacing.medium,
          }}
        />
      </Flex>
    </MenuItem>
  );
};

ProjectListProjectItem.displayName = 'ProjectListProjectItem';

export default ProjectListProjectItem;
